import React from "react";
import { useNavigate, useParams } from "react-router-dom";

const SERVICE_DATA: Record<string, any> = {
  "moving": {
    icon: "🚚", color: "#DC2626", title: "Professional Moving Services",
    subtitle: "Local and long-distance movers in Houston",
    description: "Whether you are moving across the street or across the state, local moving professionals handle your belongings with care.",
    included: ["Local moves within Houston", "Long-distance relocations", "Packing & unpacking services", "Furniture assembly & disassembly", "Apartment, house, and office moves", "Loading and unloading"],
    pricing: { typical: "$80-$150/hr", range: "Local: $80-$120/hr • Long distance: $0.50-$1/mi • Packing: $40-$60/hr" },
    faqs: [
      { q: "How far in advance should I book?", a: "We recommend booking 1-2 weeks in advance, especially during peak moving season (May-September)." },
      { q: "Are my belongings insured?", a: "Many movers carry liability insurance — confirm coverage with your pro before booking." },
      { q: "Do you provide packing supplies?", a: "Most movers can provide boxes, tape, and padding. Ask your pro when you book." },
    ]
  },
  "cleaning": {
    icon: "🧹", color: "#1A3CE8", title: "House Cleaning Services",
    subtitle: "Top-rated cleaners in Houston",
    description: "From regular maintenance cleaning to deep cleans and move-out preparation, local cleaners leave your home spotless.",
    included: ["Standard home cleaning", "Deep cleaning service", "Move in/move out cleaning", "Airbnb turnover cleaning", "Office cleaning", "Carpet & window cleaning"],
    pricing: { typical: "$80-$200/visit", range: "Standard: $80-$120 • Deep clean: $150-$250 • Move in/out: $200-$400" },
    faqs: [
      { q: "Do I need to be home during cleaning?", a: "Not at all. Many of our clients provide entry instructions and trust their cleaner." },
      { q: "Are cleaning supplies included?", a: "Yes, most cleaners bring their own supplies." },
      { q: "How long does a cleaning take?", a: "Standard cleaning takes 2-4 hours. Deep cleans take 4-8 hours." },
    ]
  },
  "electrical": {
    icon: "⚡", color: "#F5A623", title: "Electrical Services",
    subtitle: "Electricians in Houston",
    description: "From simple outlet repairs to full panel upgrades, Houston electricians handle every electrical need safely and to code.",
    included: ["Outlet & switch installation", "Lighting installation", "Ceiling fan installation", "EV charger installation", "Panel upgrades", "Generator installation", "Smart home wiring", "Emergency electrical repair"],
    pricing: { typical: "$95-$150/hr", range: "Outlet/switch: $75-$150 • Lighting: $100-$300 • Panel upgrade: $1,500-$3,000" },
    faqs: [
      { q: "Are your electricians licensed?", a: "Texas requires electrical work to be done by licensed electricians. Mouna doesn't verify licenses — ask your pro for their license number and insurance details before work begins." },
      { q: "Can you handle emergency repairs?", a: "Many of our electricians offer emergency service." },
      { q: "Do I need a permit?", a: "Major work like panel upgrades requires permits. Your electrician will handle the permitting process." },
    ]
  },
  "plumbing": {
    icon: "🔧", color: "#0E8C6A", title: "Plumbing Services",
    subtitle: "Trusted plumbers in Houston",
    description: "Leaky faucets, clogged drains, water heater issues, or full bathroom remodels — Houston plumbers handle it all.",
    included: ["Leak detection & repair", "Drain cleaning", "Water heater install/repair", "Toilet & faucet installation", "Pipe installation", "Emergency plumbing", "Garbage disposal", "Sewer line repair"],
    pricing: { typical: "$110-$180/hr", range: "Drain cleaning: $100-$300 • Water heater: $800-$2,000 • Leak repair: $150-$500" },
    faqs: [
      { q: "Do you handle emergencies 24/7?", a: "Many of our plumbers offer 24/7 emergency service." },
      { q: "Are estimates free?", a: "Most plumbers provide free estimates for non-emergency work." },
      { q: "Do you guarantee your work?", a: "Many plumbers stand behind their workmanship — ask your pro about their guarantee policy before booking." },
    ]
  },
  "outdoor": {
    icon: "🌿", color: "#16A34A", title: "Lawn & Outdoor Services",
    subtitle: "Landscaping experts in Houston",
    description: "Keep your yard looking pristine year-round. From regular lawn care to full landscape design, weve got you covered.",
    included: ["Lawn mowing & maintenance", "Tree trimming & removal", "Garden design & planting", "Mulching & edging", "Sod installation", "Irrigation systems", "Leaf removal", "Hedge trimming"],
    pricing: { typical: "$50-$120/visit", range: "Lawn mowing: $40-$80 • Tree trimming: $200-$1,000 • Landscaping: $50-$100/hr" },
    faqs: [
      { q: "Do you offer weekly service?", a: "Yes. Many lawn care pros offer weekly or bi-weekly maintenance plans." },
      { q: "Is debris removal included?", a: "Most pros include debris removal." },
      { q: "What about seasonal services?", a: "We offer fall leaf cleanup, spring landscaping prep, and winterization services." },
    ]
  },
  "painting": {
    icon: "🎨", color: "#7C3AED", title: "Painting Services",
    subtitle: "Interior and exterior painters in Houston",
    description: "Transform any room or your entire home with our skilled painters. Quality materials, clean workspace, beautiful results.",
    included: ["Interior painting", "Exterior painting", "Cabinet painting & refinishing", "Deck staining", "Wallpaper installation/removal", "Faux finishing", "Drywall repair before paint", "Color consultation"],
    pricing: { typical: "$2-$5/sq ft", range: "Interior: $2-$4/sq ft • Exterior: $3-$5/sq ft • Cabinets: $50-$100/door" },
    faqs: [
      { q: "Is paint included in the price?", a: "It varies by pro. Some include paint, others charge separately." },
      { q: "How long does interior painting take?", a: "A typical room takes 1-2 days. Whole-home interiors take 3-7 days." },
      { q: "Do you provide color consultation?", a: "Many of our painters offer free color consultations." },
    ]
  },
  "additionsremodels": {
    icon: "🏗️", color: "#0D1B3E", title: "Additions & Remodels",
    subtitle: "Home addition and remodeling experts",
    description: "Add space, value, and beauty to your home with our experienced contractors handling everything from room additions to full renovations.",
    included: ["Room additions", "Basement remodeling", "Attic conversions", "Garage conversions", "Sunroom additions", "Home extensions", "Second story additions"],
    pricing: { typical: "$100-$300/sq ft", range: "Room addition: $80-$200/sq ft • Basement: $35-$75/sq ft" },
    faqs: [
      { q: "How long does an addition take?", a: "Most room additions take 8-16 weeks from start to finish." },
      { q: "Do you handle permits?", a: "Yes, your contractor will manage all required permits." },
      { q: "Whats included in the estimate?", a: "Estimates include materials, labor, and permits unless otherwise specified." },
    ]
  },
  "airconditioning": {
    icon: "❄️", color: "#06B6D4", title: "Air Conditioning Services",
    subtitle: "AC installation, repair & maintenance",
    description: "Keep cool in Houston heat. Our HVAC experts handle AC installation, repairs, maintenance, and ductwork.",
    included: ["AC installation", "AC repair", "AC maintenance", "Duct cleaning", "Smart thermostat install", "Ventilation work", "Mini-split systems"],
    pricing: { typical: "$3,500-$8,000", range: "Install: $3,500-$8,000 • Repair: $150-$600 • Maintenance: $80-$200" },
    faqs: [
      { q: "How often should I service my AC?", a: "We recommend annual maintenance, ideally in spring before peak summer heat." },
      { q: "Whats the lifespan of an AC?", a: "Most central AC systems last 15-20 years with regular maintenance." },
      { q: "Do you offer financing?", a: "Many of our HVAC pros offer financing for new system installations." },
    ]
  },
  "bathroomremodeling": {
    icon: "🚿", color: "#8B5CF6", title: "Bathroom Remodeling",
    subtitle: "Full bathroom renovations and upgrades",
    description: "Transform your bathroom into a spa-like retreat. From simple updates to complete remodels, our pros deliver beautiful results.",
    included: ["Full bathroom remodel", "Shower installation", "Bathtub installation", "Vanity installation", "Tile work", "Plumbing fixtures", "Lighting & ventilation"],
    pricing: { typical: "$8,000-$25,000", range: "Basic update: $5,000-$10,000 • Full remodel: $15,000-$35,000" },
    faqs: [
      { q: "How long does a bathroom remodel take?", a: "Typical bathroom remodels take 2-4 weeks." },
      { q: "Whats the ROI on a bathroom remodel?", a: "Mid-range bathroom remodels typically return 60-70% of investment at sale." },
      { q: "Do I need permits?", a: "Yes, for plumbing and electrical work. Your contractor will handle them." },
    ]
  },
  "cabinets": {
    icon: "🗄️", color: "#92400E", title: "Cabinet Services",
    subtitle: "Cabinet installation and refacing",
    description: "Update your kitchen or bathroom with new cabinets. Custom builds, refacing, and professional installation.",
    included: ["Cabinet installation", "Cabinet refacing", "Custom cabinets", "Cabinet painting", "Kitchen cabinets", "Bathroom vanities", "Cabinet repair"],
    pricing: { typical: "$200-$1,200/cabinet", range: "Stock: $200-$400 • Semi-custom: $400-$800 • Custom: $800-$2,000+" },
    faqs: [
      { q: "Refacing vs replacing?", a: "Refacing costs about 50% less than replacing and takes less time." },
      { q: "How long does installation take?", a: "Most kitchen cabinet installs take 3-5 days." },
      { q: "Do you offer custom designs?", a: "Yes, many of our cabinet pros build fully custom cabinets." },
    ]
  },
  "carpentry": {
    icon: "🪵", color: "#78350F", title: "Carpentry & Woodworking",
    subtitle: "Custom woodwork and carpentry services",
    description: "Skilled carpenters for everything from custom furniture to trim work, doors, and built-in shelving.",
    included: ["Custom furniture", "Trim & molding", "Door installation", "Staircase work", "Wood repair", "Built-in shelving", "Wood decks", "Crown molding"],
    pricing: { typical: "$50-$120/hr", range: "Trim work: $5-$10/linear ft • Custom furniture: varies • Door install: $200-$600" },
    faqs: [
      { q: "Do you build custom pieces?", a: "Yes, many of our carpenters specialize in custom builds." },
      { q: "How long for trim work?", a: "Typical room of trim takes 1-2 days." },
      { q: "Can you match existing woodwork?", a: "Yes, our carpenters can match stains, profiles, and finishes." },
    ]
  },
  "concretemasonry": {
    icon: "🧱", color: "#6B7280", title: "Concrete & Masonry",
    subtitle: "Concrete, brick, and stone work",
    description: "From driveways to retaining walls, our masonry pros build strong, beautiful structures that last.",
    included: ["Concrete pouring", "Driveway repair", "Brick laying", "Retaining walls", "Walkway installation", "Stone work", "Patio installation", "Foundation repair"],
    pricing: { typical: "$8-$20/sq ft", range: "Concrete: $8-$15/sq ft • Brick: $15-$30/sq ft • Stone: $20-$40/sq ft" },
    faqs: [
      { q: "How long does concrete take to cure?", a: "Concrete reaches 70% strength in 7 days, full strength in 28 days." },
      { q: "Do you offer decorative concrete?", a: "Yes, including stamped, stained, and exposed aggregate." },
      { q: "Whats the lifespan of a concrete driveway?", a: "Properly installed concrete driveways last 30+ years." },
    ]
  },
  "deckspatios": {
    icon: "🏡", color: "#065F46", title: "Decks, Porches & Patios",
    subtitle: "Outdoor living space construction",
    description: "Create your perfect outdoor space. From simple decks to elaborate covered patios with outdoor kitchens.",
    included: ["Deck building", "Deck repair & restaining", "Patio installation", "Pergola building", "Screened porches", "Covered patios", "Deck staining", "Composite decks"],
    pricing: { typical: "$25-$50/sq ft", range: "Wood deck: $25-$35/sq ft • Composite: $35-$60/sq ft • Patio: $20-$40/sq ft" },
    faqs: [
      { q: "Wood vs composite decking?", a: "Composite costs more upfront but requires less maintenance and lasts longer." },
      { q: "Do I need a permit?", a: "Most decks over 30 inches high require permits. Your contractor will handle this." },
      { q: "How long for a new deck?", a: "Typical deck builds take 1-2 weeks." },
    ]
  },
  "drywall": {
    icon: "🏠", color: "#9CA3AF", title: "Drywall & Insulation",
    subtitle: "Drywall installation and repair",
    description: "Smooth walls and proper insulation make a beautiful, energy-efficient home. Our pros handle it all.",
    included: ["Drywall installation", "Drywall repair", "Insulation install", "Texture application", "Soundproofing", "Patch & paint", "Ceiling repair"],
    pricing: { typical: "$1.50-$3.50/sq ft", range: "Install: $1.50-$3.50/sq ft • Repair: $50-$300 per patch" },
    faqs: [
      { q: "How long for drywall to dry?", a: "Joint compound takes 24 hours per coat. Most projects take 3-5 days total." },
      { q: "Can you match existing texture?", a: "Yes, our pros can match knockdown, orange peel, and other textures." },
      { q: "Do you handle insulation upgrades?", a: "Yes, including fiberglass, cellulose, and spray foam." },
    ]
  },
  "fences": {
    icon: "🚧", color: "#B45309", title: "Fence Installation & Repair",
    subtitle: "All types of residential fencing",
    description: "Privacy, security, and curb appeal. We install and repair wood, vinyl, chain link, and iron fences.",
    included: ["Wood fence install", "Vinyl fence install", "Chain link fence", "Iron/metal fence", "Fence repair", "Gate installation", "Fence staining", "Privacy fences"],
    pricing: { typical: "$15-$45/linear ft", range: "Chain link: $15-$25 • Wood: $20-$35 • Vinyl: $25-$45 • Iron: $30-$60" },
    faqs: [
      { q: "Do I need a permit?", a: "Most cities require permits for fences over 6 feet. Your installer will handle this." },
      { q: "How long does installation take?", a: "Typical residential fence: 1-3 days." },
      { q: "Whats the best low-maintenance option?", a: "Vinyl fencing requires the least maintenance and lasts 20-30 years." },
    ]
  },
  "flooring": {
    icon: "🪵", color: "#92400E", title: "Flooring Services",
    subtitle: "All types of flooring installation",
    description: "Hardwood, laminate, tile, carpet, or vinyl — our flooring pros handle every type of installation and refinishing.",
    included: ["Hardwood installation", "Laminate flooring", "Tile flooring", "Carpet installation", "Vinyl flooring", "Floor refinishing", "Subfloor repair", "LVP install"],
    pricing: { typical: "$3-$15/sq ft", range: "Carpet: $3-$8 • Laminate: $4-$10 • Hardwood: $8-$15 • Tile: $5-$15" },
    faqs: [
      { q: "How long does installation take?", a: "Typical room: 1-2 days. Whole house: 3-7 days." },
      { q: "Can I install over existing floors?", a: "Some flooring like LVP and laminate can install over existing hard floors." },
      { q: "Whats best for high-traffic areas?", a: "Tile, LVP, and laminate are the most durable for high-traffic areas." },
    ]
  },
  "heatinghvac": {
    icon: "🔥", color: "#DC2626", title: "Heating & HVAC",
    subtitle: "Furnace and heating system experts",
    description: "Stay warm in winter. Our HVAC experts handle furnace installation, repair, and maintenance for all heating systems.",
    included: ["Furnace installation", "Furnace repair", "Boiler service", "Heat pump install", "Radiant heating", "Fireplace install", "Ductwork", "System maintenance"],
    pricing: { typical: "$3,000-$7,500", range: "Furnace install: $3,000-$7,500 • Repair: $150-$800 • Heat pump: $4,000-$8,000" },
    faqs: [
      { q: "How often should I service my furnace?", a: "Annual maintenance is recommended, ideally in fall before heating season." },
      { q: "Whats the lifespan of a furnace?", a: "Most furnaces last 15-20 years with regular maintenance." },
      { q: "Do you handle emergencies?", a: "Many of our HVAC pros offer 24/7 emergency service in winter." },
    ]
  },
  "kitchenremodeling": {
    icon: "🍳", color: "#D97706", title: "Kitchen Remodeling",
    subtitle: "Kitchen renovations and upgrades",
    description: "The heart of your home deserves the best. Full kitchen remodels, cabinet updates, countertops, and appliance installation.",
    included: ["Full kitchen remodel", "Cabinet installation", "Countertop install", "Appliance installation", "Backsplash tile", "Kitchen islands", "Lighting", "Plumbing updates"],
    pricing: { typical: "$15,000-$50,000", range: "Minor remodel: $10,000-$25,000 • Major remodel: $30,000-$75,000" },
    faqs: [
      { q: "How long does a kitchen remodel take?", a: "Most kitchen remodels take 4-8 weeks." },
      { q: "Whats the ROI?", a: "Mid-range kitchen remodels return 70-80% at sale." },
      { q: "Can I use my kitchen during the remodel?", a: "Most remodels require kitchen to be unusable for 2-3 weeks." },
    ]
  },
  "hometheater": {
    icon: "📺", color: "#4F46E5", title: "Home Theater & AV",
    subtitle: "Home theater and audio installation",
    description: "Transform any room into a cinema or concert hall. Professional installation of TVs, surround sound, and home theater systems.",
    included: ["TV mounting", "Surround sound install", "Projector setup", "Home theater design", "Cable management", "Smart home integration", "Speaker install"],
    pricing: { typical: "$200-$3,000", range: "TV mount: $150-$400 • Surround sound: $500-$2,500 • Full theater: $3,000-$15,000" },
    faqs: [
      { q: "Whats included in TV mounting?", a: "Mounting, cable concealment, and basic setup. Hardware extra." },
      { q: "Do you do whole-home audio?", a: "Yes, including outdoor speakers and multi-room systems." },
      { q: "Can you hide all the wires?", a: "Yes, our pros can run wires in walls for a clean look." },
    ]
  },
  "swimmingpools": {
    icon: "🏊", color: "#0284C7", title: "Pools, Spas & Saunas",
    subtitle: "Pool installation and maintenance",
    description: "Resort-style relaxation at home. Pool installation, repair, cleaning, and spa/sauna services.",
    included: ["Pool installation", "Pool repair & resurfacing", "Pool cleaning service", "Hot tub installation", "Sauna installation", "Pool equipment", "Pool deck"],
    pricing: { typical: "$30,000-$80,000", range: "Inground pool: $30,000-$80,000 • Hot tub: $3,000-$15,000 • Maintenance: $80-$200/mo" },
    faqs: [
      { q: "How long does pool installation take?", a: "Most inground pools take 6-12 weeks from permit to completion." },
      { q: "Whats the maintenance cost?", a: "Expect $80-$200/month for professional pool maintenance." },
      { q: "Do you offer financing?", a: "Many of our pool pros offer financing options." },
    ]
  },
  "tilestone": {
    icon: "🪨", color: "#6B7280", title: "Tile & Stone Work",
    subtitle: "Tile and natural stone installation",
    description: "Beautiful tile and stone for floors, walls, showers, and backsplashes. Quality installation that lasts.",
    included: ["Floor tile", "Wall tile", "Backsplash install", "Shower tile", "Grout repair", "Natural stone", "Tile removal", "Stone countertops"],
    pricing: { typical: "$5-$25/sq ft", range: "Ceramic: $5-$15/sq ft • Porcelain: $8-$20/sq ft • Natural stone: $15-$35/sq ft" },
    faqs: [
      { q: "How long for tile installation?", a: "Typical bathroom: 2-3 days. Kitchen backsplash: 1 day." },
      { q: "Can you match existing tile?", a: "Yes, but exact matches may not be available for older tile." },
      { q: "Whats best for showers?", a: "Porcelain and natural stone are most popular for showers." },
    ]
  },
  "windowsdoors": {
    icon: "🚪", color: "#0D1B3E", title: "Windows & Doors",
    subtitle: "Window and door installation",
    description: "Improve energy efficiency and curb appeal. New window and door installation, replacement, and repair.",
    included: ["Window installation", "Window repair", "Door installation", "Door repair", "Sliding doors", "Storm doors", "French doors", "Glass replacement"],
    pricing: { typical: "$300-$2,000 each", range: "Standard windows: $300-$1,000 • Doors: $200-$2,000 • Energy efficient: premium" },
    faqs: [
      { q: "Will new windows save money?", a: "Energy-efficient windows can reduce heating/cooling costs by 10-25%." },
      { q: "How long does install take?", a: "Most windows take 30-60 minutes each. Whole-house: 1-3 days." },
      { q: "Do you offer warranties?", a: "Yes, most installations include manufacturer warranty plus installation warranty." },
    ]
  },
  "roofing": {
    icon: "🏠", color: "#7C3AED", title: "Roofing Services",
    subtitle: "Roof installation, repair, and replacement",
    description: "Protect your home with quality roofing. Full replacements, repairs, gutter installation, and storm damage assessment.",
    included: ["Roof replacement", "Roof repair", "Gutter installation", "Gutter cleaning", "Skylight install", "Storm damage repair", "Roof inspection"],
    pricing: { typical: "$8,000-$20,000", range: "Asphalt shingle: $4-$8/sq ft • Metal: $8-$16/sq ft • Repair: $300-$1,500" },
    faqs: [
      { q: "How long does a roof last?", a: "Asphalt shingles: 20-30 years. Metal: 40-70 years. Tile: 50+ years." },
      { q: "Will insurance cover repairs?", a: "Storm damage is often covered. Get a free inspection to find out." },
      { q: "How long does replacement take?", a: "Most residential roofs are replaced in 1-3 days." },
    ]
  },
  "homeinspection": {
    icon: "🔍", color: "#059669", title: "Home Inspection",
    subtitle: "Professional home inspections",
    description: "Know what you are buying. Comprehensive inspections by certified pros for peace of mind.",
    included: ["Pre-purchase inspection", "Pre-listing inspection", "Mold inspection", "Pest inspection", "Roof inspection", "Foundation inspection", "Sewer scope"],
    pricing: { typical: "$300-$600", range: "Standard: $300-$500 • Detailed: $500-$800 • Specialty: $200-$400 each" },
    faqs: [
      { q: "How long does an inspection take?", a: "Standard inspections take 2-4 hours, with reports delivered within 24-48 hours." },
      { q: "Can I attend the inspection?", a: "Yes, we encourage buyers to attend." },
      { q: "Whats included in the report?", a: "Detailed report with photos covering structure, systems, safety, and recommendations." },
    ]
  },
  "greenhome": {
    icon: "♻️", color: "#16A34A", title: "Green Home Improvements",
    subtitle: "Eco-friendly home upgrades",
    description: "Save energy and reduce your environmental impact with sustainable home improvements.",
    included: ["Solar panel install", "Rainwater harvesting", "Green insulation", "Eco flooring", "Low-VOC painting", "Energy audits", "Smart thermostats"],
    pricing: { typical: "$1,000-$30,000", range: "Solar: $15,000-$30,000 • Insulation: $1,000-$3,000 • Energy audit: $200-$600" },
    faqs: [
      { q: "Are there tax credits?", a: "Yes, federal and Texas tax credits available for solar, energy efficiency, and EV chargers." },
      { q: "Whats the ROI on solar?", a: "Most homeowners see payback in 7-12 years with 25+ year system life." },
      { q: "Where to start?", a: "An energy audit is the best first step to identify savings opportunities." },
    ]
  },
};

export default function ServicePage() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const data = SERVICE_DATA[slug || ""] || SERVICE_DATA.moving;

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: "#FAFAFA", color: "#0D1B3E", minHeight: "100vh" }}>
      <style>{`* { box-sizing: border-box; margin: 0; padding: 0; }`}</style>

      {/* Nav */}
      <nav style={{ background: "white", borderBottom: "1px solid #F3F4F6", padding: "16px 5%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <button onClick={() => navigate("/")} style={{ background: "transparent", border: "none", cursor: "pointer", fontFamily: "Playfair Display, serif", fontSize: "1.5rem", fontWeight: 700, color: "#0D1B3E" }}>← Mouna</button>
        <button onClick={() => navigate("/login")} style={{ background: data.color, color: "white", border: "none", borderRadius: "100px", padding: "10px 24px", fontWeight: 700, fontSize: "0.875rem", cursor: "pointer" }}>Get a Quote</button>
      </nav>

      {/* Hero */}
      <div style={{ background: `linear-gradient(135deg, ${data.color}, ${data.color}DD)`, padding: "100px 5% 80px", textAlign: "center", color: "white", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", width: "400px", height: "400px", borderRadius: "50%", background: "rgba(255,255,255,0.1)", filter: "blur(80px)", top: "-100px", right: "-100px" }}/>
        <div style={{ position: "relative", maxWidth: "780px", margin: "0 auto" }}>
          <div style={{ fontSize: "4rem", marginBottom: "16px" }}>{data.icon}</div>
          <h1 style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(2.25rem, 5vw, 4rem)", fontWeight: 700, lineHeight: 1.1, marginBottom: "16px" }}>
            {data.title}
          </h1>
          <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.125rem", marginBottom: "32px" }}>{data.subtitle}</p>
          <button onClick={() => navigate("/login")} style={{ background: "white", color: data.color, border: "none", borderRadius: "100px", padding: "18px 44px", fontWeight: 700, fontSize: "1rem", cursor: "pointer", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            Get a Free Quote →
          </button>
        </div>
      </div>

      {/* Whats Included */}
      <div style={{ padding: "80px 5%", background: "white" }}>
        <div style={{ maxWidth: "1100px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, color: data.color, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "16px" }}>WHATS INCLUDED</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(2rem, 4vw, 2.75rem)", fontWeight: 700, marginBottom: "16px" }}>Services we offer</h2>
            <p style={{ color: "#6B7280", fontSize: "1rem", maxWidth: "560px", margin: "0 auto" }}>{data.description}</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: "16px" }}>
            {data.included.map((item: string) => (
              <div key={item} style={{ background: data.color + "08", border: `1px solid ${data.color}20`, borderRadius: "16px", padding: "20px 24px", display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ color: data.color, fontWeight: 700, fontSize: "1.125rem", flexShrink: 0 }}>✓</div>
                <div style={{ fontWeight: 600, fontSize: "0.9375rem" }}>{item}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Pricing snapshot */}
      <div style={{ padding: "80px 5%", background: "#F9FAFB" }}>
        <div style={{ maxWidth: "780px", margin: "0 auto", textAlign: "center" }}>
          <div style={{ fontSize: "0.7rem", fontWeight: 700, color: data.color, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "16px" }}>TYPICAL PRICING</div>
          <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(2rem, 4vw, 2.75rem)", fontWeight: 700, marginBottom: "32px" }}>Transparent rates</h2>
          <div style={{ background: "white", borderRadius: "24px", padding: "48px 32px", border: `2px solid ${data.color}20` }}>
            <div style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(2.5rem, 5vw, 3.5rem)", fontWeight: 700, color: data.color, marginBottom: "16px" }}>{data.pricing.typical}</div>
            <div style={{ color: "#6B7280", fontSize: "0.9375rem", lineHeight: 1.7 }}>{data.pricing.range}</div>
            <div style={{ height: "1px", background: "#F3F4F6", margin: "24px 0" }}/>
            <div style={{ color: "#9CA3AF", fontSize: "0.8125rem" }}>Get exact quotes from multiple pros — free, no obligation.</div>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div style={{ padding: "80px 5%", background: "white" }}>
        <div style={{ maxWidth: "780px", margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: "48px" }}>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, color: data.color, letterSpacing: "0.15em", textTransform: "uppercase", marginBottom: "16px" }}>FAQ</div>
            <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(2rem, 4vw, 2.75rem)", fontWeight: 700 }}>Common questions</h2>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            {data.faqs.map((item: any) => (
              <details key={item.q} style={{ background: "#F9FAFB", borderRadius: "16px", padding: "20px 24px", cursor: "pointer", border: "1px solid #F3F4F6" }}>
                <summary style={{ fontWeight: 700, fontSize: "1rem", color: "#0D1B3E", listStyle: "none", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  {item.q}
                  <span style={{ color: "#9CA3AF", fontSize: "1.5rem" }}>+</span>
                </summary>
                <p style={{ color: "#6B7280", fontSize: "0.9375rem", lineHeight: 1.7, marginTop: "12px" }}>{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div style={{ padding: "80px 5%", background: `linear-gradient(135deg, ${data.color}, ${data.color}DD)`, textAlign: "center", color: "white" }}>
        <div style={{ maxWidth: "600px", margin: "0 auto" }}>
          <h2 style={{ fontFamily: "Playfair Display, serif", fontSize: "clamp(2rem, 4vw, 2.75rem)", fontWeight: 700, marginBottom: "16px" }}>Ready to get started?</h2>
          <p style={{ color: "rgba(255,255,255,0.85)", fontSize: "1.0625rem", marginBottom: "32px", lineHeight: 1.7 }}>Get free quotes from top {data.title.toLowerCase()} pros in minutes.</p>
          <button onClick={() => navigate("/login")} style={{ background: "white", color: data.color, border: "none", borderRadius: "100px", padding: "16px 40px", fontWeight: 700, fontSize: "1rem", cursor: "pointer" }}>
            Get a Free Quote →
          </button>
        </div>
      </div>

      <div style={{ background: "#080F20", padding: "32px 5%", textAlign: "center", color: "rgba(255,255,255,0.4)", fontSize: "0.75rem" }}>© 2026 Mouna. All rights reserved.</div>
    </div>
  );
}
